import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, Users, Server, CreditCard, Tag, Activity, Trash2, ExternalLink,
  Plus, Search, ShieldCheck, ShieldOff, Power, Copy, X, Mail, LogOut,
} from "lucide-react";

const TABS = [
  { id: "stats",    label: "Overview",      icon: Crown },
  { id: "members",  label: "Members",       icon: Users },
  { id: "servers",  label: "Servers",       icon: Server },
  { id: "subs",     label: "Subscriptions", icon: CreditCard },
  { id: "promos",   label: "Promo Codes",   icon: Tag },
  { id: "activity", label: "Activity Log",  icon: Activity },
  { id: "interest", label: "Waitlist",      icon: Mail },
];

const TIERS = ["free", "starter", "pro", "lifetime"];

export default function AdminPanel() {
  const [tab, setTab] = useState("stats");
  const [authOk, setAuthOk] = useState(null);

  useEffect(() => {
    api.get("/admin/check").then((r) => setAuthOk(!!r.data.is_admin)).catch(() => setAuthOk(false));
  }, []);

  if (authOk === null) {
    return (
      <div className="text-zinc-500 font-mono-tabular text-sm py-20 text-center" data-testid="admin-loading">
        // VERIFYING ADMIN ACCESS...
      </div>
    );
  }

  if (!authOk) {
    return (
      <div data-testid="admin-denied" className="max-w-xl mx-auto mt-16 tactical-card p-8 text-center">
        <ShieldOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="font-heading text-2xl uppercase font-bold mb-2">Access Denied</h1>
        <p className="text-sm text-zinc-400">
          This area is restricted to platform administrators. If you believe this is a mistake, ask
          the bot application owner to add your Discord User ID to the <code className="text-brand-orange">ADMIN_DISCORD_IDS</code> environment variable.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2"><Crown className="inline w-4 h-4 mr-2 -mt-1 text-brand-orange" />SUPER ADMIN</div>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Control Panel</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">
            Top-level command of the 606 Community platform — every member, every server, every subscription, every dollar.
          </p>
        </div>
        <button
          onClick={async () => {
            if (!window.confirm("Step down from admin? You'll need to use the secret URL to come back.")) return;
            try {
              await api.post("/admin/logout");
              toast.success("Admin status revoked");
              window.location.href = "/dashboard";
            } catch { toast.error("Failed"); }
          }}
          className="btn-secondary text-xs"
          data-testid="admin-step-down"
        >
          <LogOut className="w-4 h-4" /> Step Down
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-brand-line pb-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-heading uppercase tracking-wider transition-colors ${
                active
                  ? "bg-brand-orange text-black font-bold"
                  : "text-zinc-400 hover:text-white border border-brand-line hover:border-brand-orange/50"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "stats"    && <StatsTab />}
      {tab === "members"  && <MembersTab />}
      {tab === "servers"  && <ServersTab />}
      {tab === "subs"     && <SubscriptionsTab />}
      {tab === "promos"   && <PromosTab />}
      {tab === "activity" && <ActivityTab />}
      {tab === "interest" && <InterestTab />}
    </div>
  );
}

/* ──────────────────────────  STATS  ────────────────────────── */
function StatsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-zinc-500 font-mono-tabular text-sm py-10">Loading…</div>;

  const cards = [
    { label: "Members",        value: s.users_count,            color: "text-emerald-400" },
    { label: "Active Servers", value: s.guilds_active,          color: "text-brand-orange" },
    { label: "Servers (DB)",   value: s.guilds_db,              color: "text-blue-400" },
    { label: "Members Reach",  value: s.total_members_managed,  color: "text-purple-400" },
    { label: "Promo Codes",    value: s.promo_codes_count,      color: "text-yellow-400" },
    { label: "Waitlist",       value: s.interest_count,         color: "text-pink-400" },
    { label: "Logs",           value: s.logs_count,             color: "text-zinc-400" },
    { label: "Est. MRR",       value: `$${s.estimated_mrr || 0}`,color: "text-emerald-400" },
  ];

  const breakdown = s.subscriptions_breakdown || {};

  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="tactical-card p-5">
            <div className={`font-mono-tabular text-3xl font-bold ${c.color}`}>
              {typeof c.value === "number" ? c.value.toLocaleString() : c.value}
            </div>
            <div className="overline mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="tactical-card p-6">
        <div className="overline mb-1">// SUBSCRIPTIONS</div>
        <h2 className="font-heading text-xl font-bold uppercase mb-5">Tier Breakdown</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TIERS.map((t) => (
            <div key={t} className="border border-brand-line rounded-lg p-4">
              <div className="overline">{t}</div>
              <div className="font-mono-tabular text-2xl font-bold mt-1">{breakdown[t] || 0}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────  MEMBERS  ────────────────────────── */
function MembersTab() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => {
    const url = q ? `/admin/users?q=${encodeURIComponent(q)}` : "/admin/users";
    api.get(url).then((r) => setList(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (u) => {
    setActive(u);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load user details");
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Remove this member's record? They can re-login anytime.")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Member removed");
    setActive(null);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-members">
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="admin-members-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by username…"
            className="w-full bg-brand-elevated border border-brand-line rounded-lg pl-9 pr-3 py-2 text-sm focus:border-brand-orange focus:outline-none"
          />
        </div>
        <button onClick={load} className="btn-secondary" data-testid="admin-members-search-btn">Search</button>
        <span className="ml-auto font-mono-tabular text-xs text-zinc-500">{list.length} member(s)</span>
      </div>

      <div className="tactical-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated border-b border-brand-line">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Member</th>
              <th className="px-4 py-3 overline">Discord ID</th>
              <th className="px-4 py-3 overline">Linked Servers</th>
              <th className="px-4 py-3 overline">Last Login</th>
              <th className="px-4 py-3 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500 font-mono-tabular text-sm">No members yet.</td></tr>
            )}
            {list.map((u) => (
              <tr key={u.id} className="border-b border-brand-line hover:bg-brand-elevated/40 cursor-pointer" onClick={() => openDetail(u)} data-testid={`admin-member-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {u.avatar
                      ? <img src={u.avatar} alt="" className="w-8 h-8 rounded-md object-cover" />
                      : <div className="w-8 h-8 rounded-md bg-brand-orange/20 border border-brand-orange flex items-center justify-center font-bold text-brand-orange uppercase text-xs">{u.username?.[0]}</div>
                    }
                    <span className="font-semibold">{u.username}</span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">{u.id}</td>
                <td className="px-4 py-3">
                  <span className="font-mono-tabular text-sm">{u.guilds_count ?? 0}</span>
                </td>
                <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">
                  {u.updated_at ? new Date(u.updated_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={(e) => { e.stopPropagation(); removeUser(u.id); }} className="text-red-400 hover:text-red-300 p-1" data-testid={`admin-member-delete-${u.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && (
        <MemberDetailDialog user={active} detail={detail} onClose={() => setActive(null)} />
      )}
    </div>
  );
}

function MemberDetailDialog({ user, detail, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface border border-brand-line rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto tactical-scroll" data-testid="admin-member-detail">
        <div className="flex items-center justify-between p-5 border-b border-brand-line sticky top-0 bg-brand-surface">
          <div className="flex items-center gap-3">
            {user.avatar
              ? <img src={user.avatar} alt="" className="w-10 h-10 rounded-md object-cover" />
              : <div className="w-10 h-10 rounded-md bg-brand-orange/20 border border-brand-orange flex items-center justify-center font-bold text-brand-orange uppercase">{user.username?.[0]}</div>
            }
            <div>
              <div className="font-heading text-lg font-bold uppercase">{user.username}</div>
              <div className="font-mono-tabular text-xs text-zinc-500">{user.id}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="admin-member-detail-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!detail
            ? <div className="text-zinc-500 font-mono-tabular text-sm py-8 text-center">Loading details…</div>
            : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Stat label="Total Servers" value={detail.guilds_count} />
                  <Stat label="With 606 Bot"  value={detail.bot_present_count} />
                  <Stat label="Last Login"    value={detail.updated_at ? new Date(detail.updated_at).toLocaleDateString() : "—"} small />
                </div>

                <div className="overline mt-4">// LINKED SERVERS</div>
                {detail.guilds.length === 0 && <div className="text-zinc-500 text-sm">No linked servers.</div>}
                <div className="space-y-2">
                  {detail.guilds.map((g) => (
                    <div key={g.id} className="border border-brand-line rounded-lg p-3 flex items-center gap-3" data-testid={`member-guild-${g.id}`}>
                      {g.icon
                        ? <img src={g.icon} alt="" className="w-9 h-9 rounded-md object-cover" />
                        : <div className="w-9 h-9 rounded-md bg-brand-elevated flex items-center justify-center font-bold text-zinc-400 text-xs uppercase">{g.name?.[0]}</div>
                      }
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{g.name}</div>
                        <div className="font-mono-tabular text-[10px] text-zinc-500 flex gap-2 mt-0.5">
                          <span>{g.id}</span>
                          {g.owner && <span className="text-yellow-400">OWNER</span>}
                          {g.manageable && <span className="text-blue-400">MANAGE</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {g.bot_present
                          ? <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase">Bot Active</span>
                          : <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-zinc-700/50 text-zinc-400 uppercase">No Bot</span>
                        }
                        {g.tier && (
                          <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-brand-orange/20 text-brand-orange uppercase">{g.tier}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          }
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="border border-brand-line rounded-lg p-3">
      <div className="overline">{label}</div>
      <div className={`font-mono-tabular font-bold mt-1 ${small ? "text-sm" : "text-2xl"}`}>{value ?? "—"}</div>
    </div>
  );
}

/* ──────────────────────────  SERVERS  ────────────────────────── */
function ServersTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/guilds").then((r) => setList(r.data)); }, []);

  return (
    <div className="space-y-4" data-testid="admin-servers">
      <div className="flex items-center gap-2">
        <span className="ml-auto font-mono-tabular text-xs text-zinc-500">{list.length} server(s)</span>
      </div>
      <div className="tactical-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated border-b border-brand-line">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Server</th>
              <th className="px-4 py-3 overline">Members</th>
              <th className="px-4 py-3 overline">Tier</th>
              <th className="px-4 py-3 overline">Bot Status</th>
              <th className="px-4 py-3 overline">Linked Since</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500 font-mono-tabular">No servers yet.</td></tr>}
            {list.map((g) => (
              <tr key={g.guild_id} className="border-b border-brand-line hover:bg-brand-elevated/40" data-testid={`admin-server-row-${g.guild_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {g.icon
                      ? <img src={g.icon} alt="" className="w-8 h-8 rounded-md object-cover" />
                      : <div className="w-8 h-8 rounded-md bg-brand-elevated flex items-center justify-center font-bold text-xs uppercase">{g.name?.[0]}</div>
                    }
                    <div>
                      <div className="font-semibold">{g.name}</div>
                      <div className="font-mono-tabular text-[10px] text-zinc-500">{g.guild_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-tabular">{g.member_count ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-brand-orange/20 text-brand-orange uppercase">{g.tier}</span>
                </td>
                <td className="px-4 py-3">
                  {g.bot_present
                    ? <span className="text-emerald-400 text-xs font-mono-tabular uppercase">● Active</span>
                    : <span className="text-zinc-500 text-xs font-mono-tabular uppercase">○ Offline</span>
                  }
                </td>
                <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">
                  {g.started_at ? new Date(g.started_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────────────────────────  SUBSCRIPTIONS  ────────────────────────── */
function SubscriptionsTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/subscriptions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const override = async (gid, tier) => {
    try {
      await api.post(`/admin/subscriptions/${gid}/override`, { tier });
      toast.success(`Tier updated to ${tier.toUpperCase()}`);
      load();
    } catch {
      toast.error("Override failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-subs">
      <div className="tactical-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated border-b border-brand-line">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Guild ID</th>
              <th className="px-4 py-3 overline">Current Tier</th>
              <th className="px-4 py-3 overline">Started</th>
              <th className="px-4 py-3 overline">Updated</th>
              <th className="px-4 py-3 overline">Override</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500 font-mono-tabular">No subscriptions.</td></tr>}
            {list.map((s) => (
              <tr key={s.guild_id} className="border-b border-brand-line" data-testid={`admin-sub-row-${s.guild_id}`}>
                <td className="px-4 py-3 font-mono-tabular text-xs">{s.guild_id}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-brand-orange/20 text-brand-orange uppercase">{s.tier}</span></td>
                <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">{s.started_at ? new Date(s.started_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <select
                    value={s.tier}
                    onChange={(e) => override(s.guild_id, e.target.value)}
                    className="bg-brand-elevated border border-brand-line rounded px-2 py-1 text-xs"
                    data-testid={`admin-sub-override-${s.guild_id}`}
                  >
                    {TIERS.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────────────────────────  PROMO CODES  ────────────────────────── */
function PromosTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/promo-codes").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this promo code permanently?")) return;
    await api.delete(`/admin/promo-codes/${id}`);
    toast.success("Code deleted");
    load();
  };
  const toggle = async (id) => {
    await api.post(`/admin/promo-codes/${id}/toggle`);
    load();
  };
  const copy = async (code) => {
    await navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  return (
    <div className="space-y-4" data-testid="admin-promos">
      <div className="flex items-center gap-2">
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="admin-promo-new-btn">
          <Plus className="w-4 h-4" /> New Code
        </button>
        <span className="ml-auto font-mono-tabular text-xs text-zinc-500">{list.length} code(s)</span>
      </div>

      <div className="tactical-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated border-b border-brand-line">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Code</th>
              <th className="px-4 py-3 overline">Discount</th>
              <th className="px-4 py-3 overline">Tier</th>
              <th className="px-4 py-3 overline">Uses / Limit</th>
              <th className="px-4 py-3 overline">Expires</th>
              <th className="px-4 py-3 overline">Status</th>
              <th className="px-4 py-3 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500 font-mono-tabular">No promo codes yet.</td></tr>}
            {list.map((p) => {
              const expired = p.expires_at && new Date(p.expires_at) < new Date();
              return (
                <tr key={p.id} className="border-b border-brand-line" data-testid={`admin-promo-row-${p.code}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => copy(p.code)} className="font-mono-tabular text-brand-orange font-bold hover:underline flex items-center gap-1.5">
                      {p.code} <Copy className="w-3 h-3" />
                    </button>
                    {p.description && <div className="text-xs text-zinc-500 mt-0.5">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono-tabular">{p.discount_percent}%</td>
                  <td className="px-4 py-3 font-mono-tabular text-xs uppercase">{p.valid_for_tier || "ANY"}</td>
                  <td className="px-4 py-3 font-mono-tabular text-xs">{p.uses}/{p.max_uses || "∞"}</td>
                  <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">
                    {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "Never"}
                  </td>
                  <td className="px-4 py-3">
                    {expired
                      ? <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-red-500/20 text-red-400 uppercase">Expired</span>
                      : p.enabled
                        ? <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 uppercase">Active</span>
                        : <span className="text-[10px] font-mono-tabular px-2 py-0.5 rounded bg-zinc-700/50 text-zinc-400 uppercase">Disabled</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => toggle(p.id)} className="text-zinc-400 hover:text-white p-1 inline-block" title="Toggle active" data-testid={`admin-promo-toggle-${p.code}`}>
                      <Power className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 p-1 inline-block" title="Delete" data-testid={`admin-promo-delete-${p.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && <PromoCreateDialog onClose={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function PromoCreateDialog({ onClose }) {
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(20);
  const [maxUses, setMaxUses] = useState(0);
  const [tier, setTier] = useState("");
  const [expires, setExpires] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const random = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < 8; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setCode(s);
  };

  const submit = async () => {
    if (!code.trim()) return toast.error("Code is required");
    if (discount < 1 || discount > 100) return toast.error("Discount must be 1–100%");
    setBusy(true);
    try {
      await api.post("/admin/promo-codes", {
        code: code.trim(),
        discount_percent: Number(discount),
        max_uses: Number(maxUses) || 0,
        valid_for_tier: tier || null,
        expires_at: expires ? new Date(expires).toISOString() : null,
        description: desc.trim(),
        enabled: true,
      });
      toast.success(`Promo ${code.toUpperCase()} created`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface border border-brand-line rounded-xl max-w-lg w-full p-6 space-y-4" data-testid="admin-promo-create">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold uppercase">New Promo Code</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="overline mb-1 block">Code *</label>
            <div className="flex gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GAMER20" className="flex-1 bg-brand-elevated border border-brand-line rounded-lg px-3 py-2 text-sm font-mono-tabular focus:border-brand-orange focus:outline-none" data-testid="promo-code-input" />
              <button type="button" onClick={random} className="btn-secondary text-xs">Random</button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline mb-1 block">Discount %</label>
              <input type="number" min={1} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full bg-brand-elevated border border-brand-line rounded-lg px-3 py-2 text-sm font-mono-tabular focus:border-brand-orange focus:outline-none" data-testid="promo-discount-input" />
            </div>
            <div>
              <label className="overline mb-1 block">Max Uses (0 = ∞)</label>
              <input type="number" min={0} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="w-full bg-brand-elevated border border-brand-line rounded-lg px-3 py-2 text-sm font-mono-tabular focus:border-brand-orange focus:outline-none" data-testid="promo-maxuses-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline mb-1 block">Valid For Tier</label>
              <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full bg-brand-elevated border border-brand-line rounded-lg px-3 py-2 text-sm focus:border-brand-orange focus:outline-none" data-testid="promo-tier-select">
                <option value="">Any tier</option>
                {TIERS.filter(t => t !== "free").map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="overline mb-1 block">Expires (optional)</label>
              <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="w-full bg-brand-elevated border border-brand-line rounded-lg px-3 py-2 text-sm focus:border-brand-orange focus:outline-none" data-testid="promo-expires-input" />
            </div>
          </div>

          <div>
            <label className="overline mb-1 block">Description (internal)</label>
            <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Black Friday — 20% off" className="w-full bg-brand-elevated border border-brand-line rounded-lg px-3 py-2 text-sm focus:border-brand-orange focus:outline-none" data-testid="promo-desc-input" />
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="promo-create-submit">
            {busy ? "Creating…" : "Create Code"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────  ACTIVITY  ────────────────────────── */
function ActivityTab() {
  const [logs, setLogs] = useState([]);
  const [cat, setCat] = useState("");
  const load = () => {
    const url = cat ? `/admin/activity?category=${cat}` : "/admin/activity";
    api.get(url).then((r) => setLogs(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const cats = useMemo(() => {
    const set = new Set(logs.map((l) => l.category));
    return Array.from(set);
  }, [logs]);

  return (
    <div className="space-y-4" data-testid="admin-activity">
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setCat("")} className={`text-xs px-3 py-1.5 rounded-full font-mono-tabular uppercase border ${!cat ? "bg-brand-orange text-black border-brand-orange" : "border-brand-line text-zinc-400 hover:border-brand-orange/50"}`}>All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`text-xs px-3 py-1.5 rounded-full font-mono-tabular uppercase border ${cat === c ? "bg-brand-orange text-black border-brand-orange" : "border-brand-line text-zinc-400 hover:border-brand-orange/50"}`}>{c}</button>
        ))}
        <span className="ml-auto font-mono-tabular text-xs text-zinc-500">{logs.length} events</span>
      </div>

      <div className="tactical-card p-2 max-h-[600px] overflow-y-auto tactical-scroll">
        {logs.length === 0 && <div className="text-center text-zinc-500 font-mono-tabular text-sm py-12">No events.</div>}
        <div className="space-y-1.5">
          {logs.map((l) => (
            <div key={l.id} className="border border-brand-line p-3 rounded-lg flex items-start gap-3 hover:border-brand-orange/40">
              <div className={`w-1 h-10 rounded ${
                l.level === "error" ? "bg-red-500" :
                l.level === "warning" ? "bg-yellow-500" :
                "bg-brand-orange"
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="overline text-zinc-400">{l.category}</span>
                  <span className="font-mono-tabular text-[10px] text-zinc-600">{new Date(l.timestamp).toLocaleString()}</span>
                </div>
                <div className="text-sm mt-0.5 truncate">{l.message}</div>
                <div className="font-mono-tabular text-[10px] text-zinc-500 mt-0.5">by {l.actor}{l.guild_id ? ` · ${l.guild_id}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────  WAITLIST / INTEREST  ────────────────────────── */
function InterestTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/interest").then((r) => setList(r.data)); }, []);
  return (
    <div className="space-y-4" data-testid="admin-interest">
      <div className="tactical-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated border-b border-brand-line">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Discord</th>
              <th className="px-4 py-3 overline">Email</th>
              <th className="px-4 py-3 overline">Games</th>
              <th className="px-4 py-3 overline">Message</th>
              <th className="px-4 py-3 overline">Joined</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500 font-mono-tabular">No waitlist entries yet.</td></tr>}
            {list.map((i) => (
              <tr key={i.id} className="border-b border-brand-line">
                <td className="px-4 py-3 font-semibold">{i.discord_username}</td>
                <td className="px-4 py-3 font-mono-tabular text-xs">{i.email || "—"}</td>
                <td className="px-4 py-3 text-xs">{i.favorite_games || "—"}</td>
                <td className="px-4 py-3 text-xs text-zinc-400 max-w-xs truncate" title={i.message}>{i.message || "—"}</td>
                <td className="px-4 py-3 font-mono-tabular text-xs text-zinc-400">{i.created_at ? new Date(i.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
